<template>
  <Form
    key="form"
    ref="form"
    v-slot="{ errors }"
    novalidate
    class="grid grid-cols-4 gap-3 text-blue-dark-900 sm:grid-cols-12 sm:gap-7"
    @submit="onSubmit"
  >
    <template v-for="inputField in formBlueprint.fields">
      <!-- TEXT -->
      <InputField
        v-if="
          inputField.type === 'text' ||
          inputField.type === 'email' ||
          inputField.type === 'integer'
        "
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :value="formData[inputField.handle]"
      />

      <!-- TEXTAREA -->
      <InputField
        v-if="inputField.type === 'textarea'"
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :value="formData[inputField.handle]"
      />

      <!-- SELECT -->
      <AppSelect
        v-if="inputField.type === 'select'"
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :options="inputField.config.options"
        :placeholder="$t('salutation')"
      />

      <!-- CHECKBOX GROUP -->
      <CheckboxGroup
        v-if="inputField.type === 'checkboxes'"
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :options="inputField.config.options"
        :instructions="inputField.instructions"
      />

      <!-- RADIO GROUP -->
      <RadioGroup
        v-if="inputField.type === 'radio'"
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :options="inputField.config.options"
        :instructions="inputField.instructions"
      />

      <!-- FILE UPLOAD -->
      <FileUpload
        v-if="inputField.type === 'assets'"
        :key="inputField.handle"
        :field="inputField"
        :rules="formBlueprint.rules[inputField.handle]"
        :error="errors[inputField.handle]"
        :instructions="inputField.instructions"
        multiple
      />
    </template>

    <!-- HONEYPOT -->
    <input class="hidden" :value="formBlueprint.honeypot" />

    <div
      v-if="!sent"
      class="col-span-12 mt-3 flex items-center justify-self-start sm:mt-6"
    >
      <AppButton type="submit" :is-loading="state === 'pending'">
        <slot name="submit-text">{{ $t('send') }}</slot>
      </AppButton>
    </div>

    <Transition mode="out-in">
      <AlertBanner
        v-if="sent"
        class="col-span-12 mt-10"
        :type="state"
        :success-message="successTitle || $t('form_submit_success')"
        :success-text="successText"
      />
    </Transition>

    <slot :errors="errors" />
  </Form>
</template>

<script setup>
import { Form } from 'vee-validate'
import { setLocale } from '@vee-validate/i18n'

const props = defineProps({
  formBlueprint: { type: Object, default: () => ({}) },
  formData: { type: Object, default: () => ({}) },
  disableSubmission: { type: Boolean, default: false },
  successTitle: { type: String, default: null },
  successText: { type: String, default: null },
})

const emit = defineEmits(['send'])

const config = useRuntimeConfig()
const { locale } = useI18n()
const form = ref(null)
const state = ref('idle')
const sent = ref(false)

setLocale(locale.value)

const initFormData = (fields) => {
  // convert fields to array
  fields = Object.keys(fields).map((key) => ({
    handle: key,
    value: fields[key],
  }))

  const formData = new FormData()

  fields.forEach((field) => {
    // check if value is an array of File objects
    if (field.value instanceof Array && field.value[0] instanceof File) {
      for (let i = 0; i < field.value.length; i++) {
        formData.append(field.handle, field.value[i])
      }
    } else {
      formData.append(field.handle, field.value)
    }
  })

  return formData
}

const onSubmit = async (fields) => {
  emit('send', fields)

  if (props.disableSubmission) return

  state.value = 'pending'

  try {
    await $fetch(
      `${config.public.API_URL}/!/forms/${props.formBlueprint.handle}`,
      {
        method: 'POST',
        body: initFormData(fields),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      }
    )

    sent.value = true
    state.value = 'success'
  } catch (error) {
    sent.value = true
    state.value = 'error'
    console.error(error)
  }
}

function resetForm() {
  form.value.resetForm()
}

defineExpose({
  resetForm,
})
</script>
